import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { EmulationBanner } from './components/EmulationBanner';
import { RequireAdmin } from './admin/RequireAdmin';
import { RequireArea } from './admin/RequireArea';
import { AdoptionFlow } from './adopt/AdoptionFlow';
import { MyAdoptionsPage } from './adopt/MyAdoptionsPage';
import { AgreementPage } from './adopt/AgreementPage';

// The entire admin area is code-split — a franchisee never downloads it (incl. Recharts).
const named = <T extends Record<string, unknown>>(p: Promise<T>, key: keyof T) => p.then((m) => ({ default: m[key] as React.ComponentType }));
const AdminLayout = lazy(() => named(import('./admin/AdminLayout'), 'AdminLayout'));
const DashboardPage = lazy(() => named(import('./admin/pages/DashboardPage'), 'DashboardPage'));
const EstatePage = lazy(() => named(import('./admin/pages/EstatePage'), 'EstatePage'));
const ProductsPage = lazy(() => named(import('./admin/pages/ProductsPage'), 'ProductsPage'));
const ProductDetailPage = lazy(() => named(import('./admin/pages/ProductDetailPage'), 'ProductDetailPage'));
const AdoptionLinksPage = lazy(() => named(import('./admin/pages/AdoptionLinksPage'), 'AdoptionLinksPage'));
const ResponsesPage = lazy(() => named(import('./admin/pages/ResponsesPage'), 'ResponsesPage'));
const UsersPage = lazy(() => named(import('./admin/pages/UsersPage'), 'UsersPage'));
const SettingsPage = lazy(() => named(import('./admin/pages/SettingsPage'), 'SettingsPage'));
const OpsPage = lazy(() => named(import('./admin/pages/OpsPage'), 'OpsPage'));
const ActivationPage = lazy(() => named(import('./admin/pages/ActivationPage'), 'ActivationPage'));
const VisionPage = lazy(() => named(import('./admin/pages/VisionPage'), 'VisionPage'));
const ReportsPage = lazy(() => named(import('./admin/pages/ReportsPage'), 'ReportsPage'));
const AuditPage = lazy(() => named(import('./admin/pages/AuditPage'), 'AuditPage'));
const AboutPage = lazy(() => named(import('./admin/pages/AboutPage'), 'AboutPage'));

const AdminBoot = () => (
  <div className="flex min-h-screen items-center justify-center bg-surface-canvas">
    <Loader2 className="h-6 w-6 animate-spin text-graphite-400" />
  </div>
);

function Root() {
  return (
    <AuthProvider>
      <EmulationBanner />
      <Outlet />
    </AuthProvider>
  );
}

/** Role-aware landing: internal users go to the admin area, franchisees to their adoptions. */
function Home() {
  const { user } = useAuth();
  return <Navigate to={user?.role ? '/admin' : '/my-adoptions'} replace />;
}

export const router = createBrowserRouter([
  {
    element: <Root />,
    children: [
      // Public: the adoption deep-link handles its own auth (landing → sign-in → flow).
      { path: '/adopt/:linkId', element: <AdoptionFlow /> },
      {
        element: (
          <ProtectedRoute>
            <Outlet />
          </ProtectedRoute>
        ),
        children: [
          { path: '/', element: <Home /> },
          { path: '/my-adoptions', element: <MyAdoptionsPage /> },
          { path: '/agreement/:linkId/:seq', element: <AgreementPage /> },
          {
            path: 'admin',
            element: (
              <RequireAdmin>
                <Suspense fallback={<AdminBoot />}>
                  <AdminLayout />
                </Suspense>
              </RequireAdmin>
            ),
            children: [
              { index: true, element: <RequireArea area="dashboard"><DashboardPage /></RequireArea> },
              { path: 'estate', element: <RequireArea area="estate"><EstatePage /></RequireArea> },
              { path: 'products', element: <RequireArea area="products"><ProductsPage /></RequireArea> },
              { path: 'products/:code', element: <RequireArea area="products"><ProductDetailPage /></RequireArea> },
              { path: 'links', element: <RequireArea area="links"><AdoptionLinksPage /></RequireArea> },
              { path: 'activation', element: <RequireArea area="activation"><ActivationPage /></RequireArea> },
              { path: 'responses', element: <RequireArea area="responses"><ResponsesPage /></RequireArea> },
              { path: 'reports', element: <RequireArea area="reports"><ReportsPage /></RequireArea> },
              { path: 'users', element: <RequireArea area="users"><UsersPage /></RequireArea> },
              { path: 'settings', element: <RequireArea area="settings"><SettingsPage /></RequireArea> },
              { path: 'ops', element: <RequireArea area="ops"><OpsPage /></RequireArea> },
              { path: 'audit', element: <RequireArea area="audit"><AuditPage /></RequireArea> },
              { path: 'about', element: <RequireArea area="about"><AboutPage /></RequireArea> },
            ],
          },
          {
            // Full-viewport presenter — deliberately OUTSIDE AdminLayout so the deck covers the
            // whole screen (no sidebar/header). Area-gated via the access catalog, lazy-loaded.
            path: 'admin/vision',
            element: (
              <RequireAdmin>
                <RequireArea area="vision">
                  <Suspense fallback={<AdminBoot />}>
                    <VisionPage />
                  </Suspense>
                </RequireArea>
              </RequireAdmin>
            ),
          },
        ],
      },
    ],
  },
]);
